import { FC, ReactNode } from 'react';
import { Box, IconButton, styled, SvgIconProps, Typography } from '@mui/material';
import { COLOR_ACCENT, COLOR_SECONDARY, TRANSITION_300 } from 'shared/config';

interface CircleItemProps {
  onClick: () => void;
  title: ReactNode;
  Icon: FC<SvgIconProps>;
}

export const AbsoluteWrapper = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'angle' && prop !== 'circleWidth' && prop !== 'withLine',
})<{ angle: number; circleWidth: number; withLine?: boolean }>(({ angle, circleWidth, theme, withLine }) => {
  const line = withLine
    ? {
        '&::after': {
          position: 'absolute',
          top: '50%',
          left: '50%',
          content: '""',
          width: circleWidth / 2,
          height: 2,
          transformOrigin: 'top left',
          transform: `rotate(${angle - 180}deg)`,
          backgroundImage: `linear-gradient(90deg, transparent, transparent 50%, #0F0A13 50%, #0F0A13 100%),
            linear-gradient(90deg, rgba(35, 35, 35, 0.78) 15.87%, rgba(180, 235, 86, 0.3) 90.38%);`,
          backgroundSize: '8px 2px, 100% 2px',
          zIndex: -1,
        },
      }
    : null;

  return {
    transform: `rotate(${angle}deg) translate(${circleWidth / 2}px) rotate(-${angle}deg)`,
    position: 'absolute',
    zIndex: 1,
    top: '50%',
    left: '50%',
    margin: -72 / 2,
    [theme.breakpoints.down('md')]: {
      margin: -48 / 2,
    },

    ...line,

    '& p': {
      transition: TRANSITION_300,
      position: 'absolute',
      top: '100%',
      width: 'max-content',
    },
  };
});

const Item = styled(IconButton)(({ theme }) => {
  return {
    width: 72,
    height: 72,
    transition: TRANSITION_300,

    '&:hover': {
      transform: 'scale(1.3)',

      '&+p': {
        paddingTop: 15,
        color: COLOR_ACCENT,
      },
    },

    [theme.breakpoints.down('md')]: {
      width: 48,
      height: 48,
    },

    [theme.breakpoints.down('sm')]: {
      width: 72,
      height: 72,
    },
  };
});

export const CircleItem = ({ onClick, Icon, title }: CircleItemProps) => {
  return (
    <Box display="flex" alignItems="center" justifyContent="center" flexDirection="column">
      <Item onClick={onClick} className="rounded" color="secondary">
        <Icon
          sx={{
            width: { md: '100%', sm: '22px', xs: '100%' },
          }}
        />
      </Item>
      <Typography
        pt={0.5}
        lineHeight={1.25}
        fontSize={{ xs: 12, sm: 10, md: 12 }}
        textAlign="center"
        color={COLOR_SECONDARY}
      >
        {title}
      </Typography>
    </Box>
  );
};
